import { useDispatch, useSelector } from "react-redux";
import { BrandsEndPointLogic } from "../../../../../logic/Partners/DataIntegrations/BrandsEndPointLogic";
import {
  setIsFetchedBrandsData,
  setIsLoadingBrandsData,
} from "../../../../../features/brandsSlice";
import {
  selectUserToken,
  selectUserWebsitePermissions,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { BRANDS_QUERY_ID } from "../constants";

export const useBrands = () => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const websitePermissions = useSelector(selectUserWebsitePermissions) as any;
  const webSiteIds = websitePermissions?.map(
    (item: any) => Object.keys(item)[0],
  ); // Every website shared UID the user has permission for
  const type = "site";

  const {
    allBrandsData,
    brandsMetadata,
    isFetchedBrandsData,
    isLoadingBrandsData,
  } = useSelector((state: any) => state.brands);

  // Exposed so other Brands-module actions (e.g. Try to Connect) can show
  // the same whole-tab BrandsSkeleton — used for the initial load — while
  // their own request is in flight, not just while this hook's own fetch
  // runs.
  const beginLoading = () => {
    dispatch(setIsFetchedBrandsData(false));
    dispatch(setIsLoadingBrandsData(true));
  };
  const endLoading = () => {
    dispatch(setIsFetchedBrandsData(true));
    dispatch(setIsLoadingBrandsData(false));
  };

  const fetchedBrandsData = async () => {
    // No website permissions to derive UIDs from — don't send the API call a
    // broken/empty website_shared_uid array. Surface it as an error and fall
    // through to the tabs' existing empty state instead of leaving the page
    // stuck on the loading skeleton.
    if (!webSiteIds?.length) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Unable to load brands: no website permissions found.",
          type: "error",
        }),
      );
      dispatch(setIsFetchedBrandsData(true));
      dispatch(setIsLoadingBrandsData(false));
      return;
    }

    dispatch(setIsFetchedBrandsData(false));
    dispatch(setIsLoadingBrandsData(true));
    try {
      await BrandsEndPointLogic.getAllBrandsData(
        oauthTokenHeader,
        BRANDS_QUERY_ID,
        webSiteIds,
        type,
      );
    } catch (e) {
      dispatch(
        setDisplayErrorSnackBar({
          message: "Failed to access brands data.",
          type: "error",
        }),
      );
    } finally {
      dispatch(setIsFetchedBrandsData(true));
      dispatch(setIsLoadingBrandsData(false));
    }
  };

  return {
    fetchedBrandsData,
    beginLoading,
    endLoading,
    allBrandsData,
    brandsMetadata,
    isFetchedBrandsData,
    isLoadingBrandsData,
  };
};
